import React, { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import '@mb/mb-api';
import { ERoutesCommon, ERoutesFranchiseePrivate, ERoutesPrivate, ERoutesPublic, RoutesCirPrefix } from 'common/models/routesModel';
import { PermissionLayout } from 'common/components/Layouts/PermissionLayout';
import { Header } from 'common/components/Header/Header';
import { BasicLayout } from 'common/components/Layouts/BasicLayout';
import { RootPublicPage } from 'common/components/Pages/RootPublicPage';
import { RoleCheckLayout } from 'common/components/Layouts/RoleCheckLayout';
import { PublicLayout } from 'common/components/Layouts/PublicLayout';
import { RootFranchiseePage } from 'common/components/Pages/RootFranchiseePage';
import { RootFranchisorPage } from 'common/components/Pages/RootFranchisorPage';
import { HeaderFranchisee } from 'common/components/Header/HeaderFranchisee';
import { RootSalesOpsPage } from 'common/components/Pages/RootSalesOpsPage';
import { isCirEnabled } from 'common/config/env.config';
import { CirLayout } from 'common/components/Layouts/CirLayout';
import { hasAccess, hasAnyCirAccess, isFranchisorRole, isSuperAdminRole } from 'common/helpers/permission.helper';
import { MenuPage } from 'app/pages/Franchisor/MenuPage';
import { UserTablePage } from 'app/pages/Franchisor/UserTablePage';
import { StoreTablePage } from 'app/pages/Franchisor/StoreTablePage';
import { StorePage } from 'app/pages/Franchisor/StorePage';
import { UserProfilePage } from 'app/pages/Franchisor/UserProfilePage';
import { CategoryPage } from 'app/pages/Franchisor/CategoryPage';
import { UserPage } from 'app/pages/Franchisor/UserPage';
import { MenuItemPage } from 'app/pages/Franchisor/MenuItemPage';
import { AssetPage } from 'app/pages/Franchisor/AssetPage';
import { StatisticFranchiseePage } from 'app/pages/Franchisee/StatisticFranshiseePage';
import { ProfileFranchiseePage } from 'app/pages/Franchisee/ProfileFranchiseePage';
import { StoreListFranchiseePage } from 'app/pages/Franchisee/StoreListFranchiseePage';
import { CustomerTablePage } from 'app/pages/Franchisor/CustomerTablePage';
import { StoreOrderPage } from 'app/pages/Franchisee/StoreOrderPage';
import { CustomerPage } from 'app/pages/Franchisor/CustomerPage';
import { StatisticFranchisorPage } from 'app/pages/Franchisor/StatisticFranchisorPage';
import { TechnicalWorkPage } from 'app/pages/Common/TechnicalWorkPage';
import { StoreFeedbackPage } from 'app/pages/Franchisee/StoreFeedbackPage';
import { RatingPage } from 'app/pages/Franchisor/RatingPage';
import { RatingTablePage } from 'app/pages/Franchisor/RatingTablePage';
import { PromoTablePage } from 'app/pages/Franchisor/PromoTablePage';
import { PromoPage } from 'app/pages/Franchisor/PromoPage';
import { QRTablePage } from 'app/pages/Franchisor/QRTablePage';
import { QRPage } from 'app/pages/Franchisor/QRPage';
import { BitecoinPage } from 'app/pages/Franchisor/BitecoinPage';
import { BitecoinRewardCategoryPage } from 'app/pages/Franchisor/BitecoinRewardCategoryPage';
import { BitecoinRewardPage } from 'app/pages/Franchisor/BitecoinRewardPage';
import { StatisticSalesOpsPage } from 'app/pages/SalesOps/StatisticSalesOpsPage';
import { APIKeysPage } from 'app/pages/Franchisee/APIKeysPage';
import { StoreListSalesOpsPage } from 'app/pages/SalesOps/StoreListSalesOpsPage';
import { useAppDispatch } from 'app/store/store.hooks';
import { initAuthModel } from 'app/store/reducers/auth.reducer';
import { AnnouncementTablePage } from 'app/pages/Franchisor/AnnouncementTablePage';
import { AnnouncementPage } from 'app/pages/Franchisor/AnnouncementPage';
import { RegionsGroupsPage } from 'app/pages/Franchisor/RegionsGroupsPage';
import { useAppSelector } from 'app/store/store.hooks';
import { EPermissionTab } from 'entities/Permission/Permission.models';
import { PasswordForgotPage } from 'entities/Auth/components/PasswordForgotPage';
import { LoginPage } from 'entities/Auth/components/LoginPage';
import { PasswordRestorePage } from 'entities/Auth/components/PasswordRestorePage';
import { SignUpConfirmPage } from 'entities/Auth/components/SignUpConfirmPage';
import { FranchiseeRegisterPage } from 'entities/Auth/components/FranchiseeRegisterPage';
import { TwoFactorAuthSetupModal } from 'entities/User/components/TwoFactorAuthSetupModal';
import { EUserRole } from 'entities/User/User.models';

export const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((state) => state.auth.authUser);
  const isSuperAdmin = isSuperAdminRole(authUser?.role);
  const isFranchisor = isFranchisorRole(authUser?.role);
  const permissions = authUser?.permissions;

  // Mirror of the Header gating: the permission overlay only narrows a franchisor with a profile.
  // superadmin sees everything; a plain franchisor without permissions keeps legacy full access.
  const canSee = (tab: EPermissionTab): boolean => {
    if (isSuperAdmin) {
      return true;
    }

    if (isFranchisor && permissions) {
      return hasAccess(permissions, tab);
    }

    return true;
  };

  // MBC-84 Phase 2: CIR routes for franchisor/superadmin — superadmin always, franchisor only
  // when the profile grants at least one CIR module (no profile-less fallback).
  const canSeeCir = isSuperAdmin || hasAnyCirAccess(permissions);

  useEffect(() => {
    dispatch(initAuthModel());
  }, []);

  return (
    <PermissionLayout
      publicRoutes={
        <Switch>
          <Route path={ERoutesCommon.TechnicalWork} component={TechnicalWorkPage} />
          <Route path={`${ERoutesCommon.Root}*`}>
            <PublicLayout>
              <Switch>
                <Route path={ERoutesPublic.Login} component={LoginPage} />
                <Route path={ERoutesPublic.PasswordForgot} component={PasswordForgotPage} />
                <Route path={ERoutesPublic.PasswordRestore} component={PasswordRestorePage} />
                <Route path={ERoutesPublic.SignUpConfirm} component={SignUpConfirmPage} />
                <Route path={ERoutesPublic.FranchiseeRegister} component={FranchiseeRegisterPage} />
                <Route path={`${ERoutesCommon.Root}*`} component={RootPublicPage} />
              </Switch>
            </PublicLayout>
          </Route>
        </Switch>
      }
      privateRoutes={
        <RoleCheckLayout
          franchisorRoutes={
            <>
              <TwoFactorAuthSetupModal />

              <Switch>
                {isCirEnabled && canSeeCir && (
                  <Route path={`${RoutesCirPrefix}/:segments*`} component={() => <CirLayout />} />
                )}
                <Route path={ERoutesCommon.TechnicalWork} component={TechnicalWorkPage} />
                <Route path={`${ERoutesCommon.Root}*`}>
                  <Header />
                  <BasicLayout id="basicLayout" layoutType={EUserRole.Franchisor}>
                    <Switch>
                      {canSee(EPermissionTab.Statistics) && (
                        <Route path={ERoutesPrivate.Statistics} component={StatisticFranchisorPage} />
                      )}
                      {/* MBC-84 Phase 2: Portal (Revenue) — the salesop statistic pages reused for
                          franchisor profiles with the Portal tab (data is scoped by store scope on BE). */}
                      {canSee(EPermissionTab.Portal) && (
                        <Route path={ERoutesFranchiseePrivate.StoreList} component={StoreListSalesOpsPage} />
                      )}
                      {canSee(EPermissionTab.Portal) && (
                        <Route path={`${ERoutesFranchiseePrivate.StorePage}/:id`} component={StoreOrderPage} />
                      )}
                      {/* MBC-84 Phase 2 (review, finding 1): the page data (GET /ratings) requires the
                          Feedback tab — a profile without it (e.g. Sales Ops) must not reach a 403 page. */}
                      {canSee(EPermissionTab.Portal) && canSee(EPermissionTab.Feedback) && (
                        <Route
                          path={`${ERoutesFranchiseePrivate.StoreFeedback}/:id`}
                          render={(routeProps) => <StoreFeedbackPage {...routeProps} isSalesOps={true} />}
                        />
                      )}
                      {canSee(EPermissionTab.Portal) && !canSee(EPermissionTab.Statistics) && (
                        <Route path={ERoutesFranchiseePrivate.Statistics} component={StatisticSalesOpsPage} />
                      )}
                      {/* User Management — superadmin + franchisor with the Users tab (mirrors API @RequireModule). */}
                      {canSee(EPermissionTab.Users) && <Route path={ERoutesPrivate.UsersInvite} component={UserPage} />}
                      {canSee(EPermissionTab.Users) && <Route path={`${ERoutesPrivate.Users}/:id`} component={UserPage} />}
                      {canSee(EPermissionTab.Users) && <Route path={ERoutesPrivate.Users} component={UserTablePage} />}
                      {canSee(EPermissionTab.Feedback) && (
                        <Route path={`${ERoutesPrivate.Rating}/:id`} component={RatingPage} />
                      )}
                      {canSee(EPermissionTab.Feedback) && (
                        <Route path={ERoutesPrivate.Rating} component={RatingTablePage} />
                      )}
                      {canSee(EPermissionTab.Menu) && <Route path={ERoutesPrivate.Menu} component={MenuPage} />}
                      {canSee(EPermissionTab.Menu) && (
                        <Route path={ERoutesPrivate.CategoryCreate} component={CategoryPage} exact />
                      )}
                      {canSee(EPermissionTab.Menu) && (
                        <Route path={`${ERoutesPrivate.Category}/:id`} component={CategoryPage} exact />
                      )}
                      {canSee(EPermissionTab.Menu) && (
                        <Route path={ERoutesPrivate.AssetCreate} component={AssetPage} exact />
                      )}
                      {canSee(EPermissionTab.Menu) && (
                        <Route path={`${ERoutesPrivate.Asset}/:id`} component={AssetPage} exact />
                      )}
                      {canSee(EPermissionTab.Stores) && (
                        <Route path={ERoutesPrivate.Stores} component={StoreTablePage} exact />
                      )}
                      {canSee(EPermissionTab.Stores) && (
                        <Route path={ERoutesPrivate.StoresCreate} component={StorePage} exact />
                      )}
                      {canSee(EPermissionTab.Stores) && (
                        <Route path={`${ERoutesPrivate.Stores}/:id`} component={StorePage} exact />
                      )}
                      {canSee(EPermissionTab.Promos) && (
                        <Route path={ERoutesPrivate.Promo} component={PromoTablePage} exact />
                      )}
                      {canSee(EPermissionTab.Promos) && (
                        <Route path={ERoutesPrivate.PromoCreate} component={PromoPage} exact />
                      )}
                      {canSee(EPermissionTab.Promos) && (
                        <Route path={`${ERoutesPrivate.Promo}/:id`} component={PromoPage} exact />
                      )}
                      {canSee(EPermissionTab.Customers) && (
                        <Route path={ERoutesPrivate.Customers} component={CustomerTablePage} exact />
                      )}
                      {canSee(EPermissionTab.Customers) && (
                        <Route path={`${ERoutesPrivate.Customers}/:id`} component={CustomerPage} exact />
                      )}
                      <Route path={ERoutesPrivate.PasswordAndSecurity} component={UserProfilePage} />
                      {canSee(EPermissionTab.Menu) && (
                        <Route path={ERoutesPrivate.MenuItemCreate} component={MenuItemPage} exact />
                      )}
                      {canSee(EPermissionTab.Menu) && (
                        <Route path={`${ERoutesPrivate.MenuItem}/:id`} component={MenuItemPage} exact />
                      )}
                      {canSee(EPermissionTab.QR) && <Route path={ERoutesPrivate.QR} component={QRTablePage} exact />}
                      {canSee(EPermissionTab.QR) && (
                        <Route path={ERoutesPrivate.QRCreate} component={QRPage} exact />
                      )}
                      {canSee(EPermissionTab.QR) && (
                        <Route path={`${ERoutesPrivate.QR}/:id`} component={QRPage} exact />
                      )}
                      {canSee(EPermissionTab.Bitecoins) && (
                        <Route path={ERoutesPrivate.Bitecoin} component={BitecoinPage} exact />
                      )}
                      {canSee(EPermissionTab.Bitecoins) && (
                        <Route
                          path={ERoutesPrivate.BitecoinRewardCategoryCreate}
                          component={BitecoinRewardCategoryPage}
                          exact
                        />
                      )}
                      {canSee(EPermissionTab.Bitecoins) && (
                        <Route
                          path={`${ERoutesPrivate.BitecoinRewardCategory}/:id`}
                          component={BitecoinRewardCategoryPage}
                          exact
                        />
                      )}
                      {canSee(EPermissionTab.Bitecoins) && (
                        <Route path={ERoutesPrivate.BitecoinRewardCreate} component={BitecoinRewardPage} exact />
                      )}
                      {canSee(EPermissionTab.Bitecoins) && (
                        <Route path={`${ERoutesPrivate.BitecoinReward}/:id`} component={BitecoinRewardPage} exact />
                      )}
                      {canSee(EPermissionTab.Announcements) && (
                        <Route path={ERoutesPrivate.Announcements} component={AnnouncementTablePage} exact />
                      )}
                      {canSee(EPermissionTab.Announcements) && (
                        <Route path={ERoutesPrivate.AnnouncementsCreate} component={AnnouncementPage} exact />
                      )}
                      {canSee(EPermissionTab.Announcements) && (
                        <Route path={`${ERoutesPrivate.Announcements}/:id`} component={AnnouncementPage} exact />
                      )}
                      {canSee(EPermissionTab.RegionsGroups) && (
                        <Route path={ERoutesPrivate.RegionsGroups} component={RegionsGroupsPage} exact />
                      )}
                      <Route path={`${ERoutesCommon.Root}*`} component={RootFranchisorPage} />
                    </Switch>
                  </BasicLayout>
                </Route>
              </Switch>
            </>
          }
          franchiseeRoutes={
            <>
              <TwoFactorAuthSetupModal />

              <Switch>
                {isCirEnabled && <Route path={`${RoutesCirPrefix}/:segments*`} component={() => <CirLayout />} />}
                <Route path={ERoutesCommon.TechnicalWork} component={TechnicalWorkPage} />
                <Route path={`${ERoutesCommon.Root}*`}>
                  <HeaderFranchisee />
                  <BasicLayout layoutType={EUserRole.Franchisee}>
                    <Switch>
                      <Route path={ERoutesFranchiseePrivate.StoreList} component={StoreListFranchiseePage} />
                      <Route path={`${ERoutesFranchiseePrivate.StorePage}/:id`} component={StoreOrderPage} />
                      <Route path={ERoutesFranchiseePrivate.Statistics} component={StatisticFranchiseePage} />
                      <Route path={ERoutesFranchiseePrivate.APIKeys} component={APIKeysPage} />
                      <Route path={ERoutesFranchiseePrivate.PasswordAndSecurity} component={ProfileFranchiseePage} />
                      <Route path={`${ERoutesFranchiseePrivate.StoreFeedback}/:id`} component={StoreFeedbackPage} />
                      <Route path={`${ERoutesCommon.Root}*`} component={RootFranchiseePage} />
                    </Switch>
                  </BasicLayout>
                </Route>
              </Switch>
            </>
          }
          salesOpsRoutes={
            <>
              <TwoFactorAuthSetupModal />

              <Switch>
                <Route path={ERoutesCommon.TechnicalWork} component={TechnicalWorkPage} />
                <Route path={`${ERoutesCommon.Root}*`}>
                  <HeaderFranchisee />
                  <BasicLayout id="basicLayout" layoutType={EUserRole.SalesOps}>
                    <Switch>
                      <Route path={ERoutesFranchiseePrivate.StoreList} component={StoreListSalesOpsPage} />
                      <Route path={`${ERoutesFranchiseePrivate.StorePage}/:id`} component={StoreOrderPage} />
                      <Route
                        path={`${ERoutesFranchiseePrivate.StoreFeedback}/:id`}
                        render={(props) => <StoreFeedbackPage {...props} isSalesOps={true} />}
                      />
                      <Route path={ERoutesFranchiseePrivate.Statistics} component={StatisticSalesOpsPage} />
                      <Route path={ERoutesFranchiseePrivate.PasswordAndSecurity} component={ProfileFranchiseePage} />
                      <Route path={`${ERoutesCommon.Root}*`} component={RootSalesOpsPage} />
                    </Switch>
                  </BasicLayout>
                </Route>
              </Switch>
            </>
          }
        />
      }
    />
  );
};
